import "react-tooltip/dist/react-tooltip.css";

import GameMap from "../../components/GameMap/GameMap";
import loadGame, { loadFlightPath } from "../../hooks/LoadGame";
import ControlContainer from "../../components/ControlContainer/ControlContainer";
import GameInfo from "../../components/GameInfo/GameInfo";
import { useEffect, useReducer, useRef, useState } from "react";
import { roundReducer, RoundActionType } from "../../reducers/RoundReducer";
import {
  DialogBody,
  DialogContent,
  DialogRoot,
} from "../../components/ui/dialog";
import { RoundSummary } from "../../components/RoundSummary/RoundSummary";
import { GameSummary } from "../../components/GameSummary/GameSummary";
import {
  eventCategories,
  incrementProfileProperty,
  logEvent,
  trackEvent,
} from "@/utils/analytics";
import { completeGame } from "@/services/game";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import { TipOverlay, TipType } from "@/components/TipOverlay/TipOverlay";
import { Snowfall } from "react-snowfall";
import { Box } from "@chakra-ui/react";
import { useBeforeUnload } from "react-router-dom";
import { Ad } from "@/components/Ad/ad";

interface GuessProps {
  airport_guess: string;
  type: "origin" | "destination";
}

const regionCenters = {
  usa: { lat: 41.978611, lng: -87.904724 },
  eur: { lat: 51.5074, lng: -0.1278 },
  world: { lat: 0, lng: 0 },
};

function Play() {
  const difficulty =
    (localStorage.getItem("selected_difficulty") as
      | "easy"
      | "medium"
      | "hard") || "easy";

  const region =
    (localStorage.getItem("selected_region") as "usa" | "eur" | "world") ||
    "usa";

  const { airports, flights, gameId, numberOfGamesPlayedByUser, gameError } =
    loadGame(difficulty, region);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [roundCompleted, setRoundCompleted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [tipType, setTipType] = useState<TipType | null>(null);
  const [round, dispatchRound] = useReducer(roundReducer, {
    score: 1000,
    hints: {},
    flight: undefined,
    airportStates: {},
    airports: undefined,
    originState: "not_set",
    destinationState: "not_set",
    lastGuess: null,
    originGuessDistance: -1,
    destinationGuessDistance: -1,
    originGuess: "",
    destinationGuess: "",
    airportSuggestions: [],
    incorrectGuessCount: 0,
  });

  const isSantaFlight = round.flight?.flight_iata === "HOHO1";

  useEffect(() => {
    logEvent(eventCategories.play.game.start);

    setCurrentRound(0);
  }, [flights]);

  useEffect(() => {
    if (gameError) {
      setTipType("game-error");
    }
  }, [gameError]);

  useEffect(() => {
    if (flights.length <= currentRound) {
      return;
    }

    setRoundCompleted(false);
    const selectedFlight = flights[currentRound];

    dispatchRound({
      type: RoundActionType.SetFlight,
      payload: selectedFlight,
    });

    const fetchFlightPathBlock = async () => {
      const flightPath = await loadFlightPath(selectedFlight);
      dispatchRound({
        type: RoundActionType.SetFlightPath,
        payload: flightPath,
      });
    };

    fetchFlightPathBlock();

    logEvent(eventCategories.play.round.start);
    incrementProfileProperty("rounds_started");
  }, [flights, currentRound]);

  useEffect(() => {
    if (airports) {
      dispatchRound({
        type: RoundActionType.SetAirports,
        payload: airports,
      });
    }
  }, [airports]);

  useEffect(() => {
    setTimeout(() => {
      setRoundCompleted(
        round.originState === "success" && round.destinationState === "success"
      );

      if (
        round.originState === "success" &&
        round.destinationState === "success"
      ) {
        logEvent(eventCategories.play.round.complete);
        trackRoundComplete({ givingUp: false });
      }
    }, 500);
  }, [round.originState, round.destinationState]);

  // If the difficulty is easy and the round is the first round, show the tip overlay
  useEffect(() => {
    if (currentRound === 0 && numberOfGamesPlayedByUser === 1) {
      const tipTimer = setTimeout(() => {
        setTipType("intial-instructions");
      }, 3000);

      return () => clearTimeout(tipTimer);
    }
  }, [numberOfGamesPlayedByUser]);

  useEffect(() => {
    if (
      round.incorrectGuessCount === 3 &&
      currentRound === 0 &&
      numberOfGamesPlayedByUser === 1
    ) {
      const tipTimer = setTimeout(() => {
        setTipType("suggest-hint");
      }, 1000);

      return () => clearTimeout(tipTimer);
    }
  }, [round.incorrectGuessCount]);

  useEffect(() => {
    if (round.lastGuess) {
      trackEvent("guess_submitted", {
        game_mode: "classic",
        guess: round.lastGuess.airport.iata,
        answer:
          round.lastGuess.type === "origin"
            ? round.flight?.origin_airport.iata
            : round.flight?.destination_airport.iata,
        type: round.lastGuess.type,
        is_correct: round.lastGuess.isCorrect,
      });
      logEvent(eventCategories.play.guess.submit);
    }
  }, [round.lastGuess]);

  useBeforeUnload((event) => {
    if (!round.flight && !gameError) {
      event.preventDefault();
    }
  });

  const onRevealHint = (hint: string) => {
    logEvent(eventCategories.play.hint.reveal, hint);
    trackEvent("hint_revealed", {
      game_mode: "classic",
      difficulty,
      region,
      hint,
    });

    dispatchRound({
      type: RoundActionType.RevealHint,
      payload: hint,
    });
  };

  const onSubmitGuess = (guess: GuessProps) => {
    dispatchRound({
      type: RoundActionType.SubmitGuess,
      payload: guess,
    });
  };

  const trackRoundComplete = ({ givingUp }: { givingUp: boolean }) => {
    if (!round.flight) {
      return;
    }

    trackEvent("round_completed", {
      game_mode: "classic",
      success: !givingUp,
      correct_guesses:
        (round.originState === "success" ? 1 : 0) +
        (round.destinationState === "success" ? 1 : 0),
      score: givingUp ? 0 : round.score,
      origin_airport: round.flight?.origin_airport.iata,
      destination_airport: round.flight?.destination_airport.iata,
      total_guesses: Object.values(round.airportStates).filter(
        (state) => state.state !== "default"
      ).length,
      hints: Object.keys(round.hints).length,
      region,
      difficulty,
      lat_lon: {
        lat: round.flight.position.lat,
        lon: round.flight.position.lon,
      },
    });

    incrementProfileProperty("rounds_completed");
  };

  const onGiveUp = () => {
    logEvent(eventCategories.play.round.giveUp);

    trackRoundComplete({ givingUp: true });

    dispatchRound({
      type: RoundActionType.GiveUp,
      payload: null,
    });

    setRoundCompleted(true);
  };

  const onNextRound = () => {
    setTotalPoints(totalPoints + round.score);
    setRoundCompleted(false);

    if (currentRound < flights.length - 1) {
      setCurrentRound(currentRound + 1);
    } else {
      setGameCompleted(true);

      logEvent(eventCategories.play.game.complete);

      if (gameId) {
        completeGame(gameId, totalPoints + round.score);
      }
    }
  };

  const onResetGame = () => {
    window.location.reload();
  };

  const roundStateRef = useRef(round);

  useEffect(() => {
    roundStateRef.current = round;
  }, [round]);

  return (
    <Box position="relative" w="100vw" h="100vh">
      <Box
        display={{ base: "none", md: "flex" }}
        justifyContent="center"
        position="absolute"
        top={0}
        left={0}
        right={0}
        zIndex={1001}
        visibility={roundCompleted || gameCompleted ? "hidden" : "visible"}
      >
        <Ad mt={5} slot="1808624891" height="50px" width="400px" />
      </Box>
      <LoadingOverlay
        visible={
          (airports.length === 0 || flights.length === 0) && gameError === null
        }
      />
      {tipType && (
        <TipOverlay
          visible={tipType !== null}
          type={tipType}
          suggestedAirports={round.airportSuggestions}
          onClick={() => {
            // If the tip is the initial instructions, queue up the "suggest-origin" tip if the user has not guessed correctly yet
            if (
              tipType === "intial-instructions" &&
              round.airportSuggestions.length > 0
            ) {
              setTimeout(() => {
                const currentRoundState = roundStateRef.current;

                if (
                  currentRoundState.originState !== "success" &&
                  currentRoundState.destinationState !== "success"
                ) {
                  setTipType("suggest-origin");
                }
              }, 10000);
            } else if (tipType === "game-error") {
              window.location.reload();
            }

            setTipType(null);
          }}
        />
      )}
      <DialogRoot lazyMount open={roundCompleted || gameCompleted} size="full">
        {roundCompleted ? (
          <DialogContent background="blackAlpha.800">
            <DialogBody
              w="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {round.flight && (
                <RoundSummary
                  finalRoundComplete={currentRound === flights.length - 1}
                  points={round.score}
                  flight={round.flight}
                  onNextRound={onNextRound}
                />
              )}
            </DialogBody>
          </DialogContent>
        ) : (
          <DialogContent background="blackAlpha.800">
            <DialogBody
              w="100%"
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
              overflowY="auto"
            >
              <GameSummary
                points={totalPoints}
                flights={flights}
                onResetGame={onResetGame}
              />
            </DialogBody>
          </DialogContent>
        )}
        <GameInfo
          points={round.score.toLocaleString(navigator.language)}
          rounds={`${currentRound + 1}/${flights.length}`}
        />
        <GameMap
          theme={isSantaFlight ? "dark" : "light"}
          centerLat={regionCenters[region]?.lat || 0}
          centerLng={regionCenters[region]?.lng || 0}
          airports={airports.map((airport) => ({
            airport,
            state: round.airportStates[airport.iata]?.state || "default",
            distance: round.airportStates[airport.iata]?.distance,
          }))}
          currentFlight={round.flight || null}
          path={round.flightPath || []}
          latestGuess={round.lastGuess}
          showAirports={difficulty !== "hard"}
        />
        {isSantaFlight && <Snowfall snowflakeCount={200} />}
        <ControlContainer
          airports={airports}
          airline={round.hints.airline || ""}
          altitude={round.hints.altitude || ""}
          duration={round.hints.duration || ""}
          originTimezone={round.hints.originTimezone || ""}
          destinationTimezone={round.hints.destinationTimezone || ""}
          onRevealHint={onRevealHint}
          onGiveUp={onGiveUp}
          onShowInfo={() => setTipType("info-instructions")}
          originInput={{
            onSubmitGuess: onSubmitGuess,
            type: "origin",
            airports: airports,
            value: round.originGuess,
            status: round.originState,
            guessDistance: round.originGuessDistance,
          }}
          destinationInput={{
            onSubmitGuess: onSubmitGuess,
            type: "destination",
            airports: airports,
            value: round.destinationGuess,
            status: round.destinationState,
            guessDistance: round.destinationGuessDistance,
          }}
        />
      </DialogRoot>
    </Box>
  );
}

export default Play;
